import {TabProps, TabsHeader} from 'platform/components';
import {HStack, Show, Space} from 'platform/foundation';

import {useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda-adjunct';

import {selectSaleVehicle} from '@dms/api/vehicle';
import {useGetServiceVehicleQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {buildArray, composePath} from 'shared/web';

import {useRouter} from '../../hooks/useRouter';
import {selectActiveAuditId, selectInspections} from '../../store/carAudit/selectors';
import {InspectionIdForAuditId} from '../../types/InspectionIdForAuditId';
import {Audit} from './components/Audit';
import {ConditionHeaderPortal} from './components/ConditionHeaderPortal';
import {InspectionHeader} from './components/InspectionHeader';
import {Inspections} from './components/Inspections';
import {NewInspectionButton} from './components/NewInspectionButton';
import {useHandleForeignInspection} from './hooks/useHandleForeignInspection';

const TAB_IDS = {
  VEHICLE_CONDITION: 'vehicleCondition',
  INSPECTIONS: 'inspections',
} as const;

interface NavigationTab {
  id: string;
  label: string;
  route: string;
}

export function VehicleInspection(props: Omit<InspectionIdForAuditId, 'auditId'>) {
  const router = useRouter();
  const {id, inspectionId} = useParams();
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);
  const {data: saleVehicle} = useSelector(selectSaleVehicle(id ?? ''));
  const isSaleVehicle = isNotNil(saleVehicle);

  const {data: serviceVehicle} = useGetServiceVehicleQuery({vehicleId: id ?? ''});
  const isServiceVehicle = isNotNil(serviceVehicle);

  const inspections = useSelector(selectInspections);
  const activeInspectionId = useSelector(selectActiveAuditId);
  const currentInspection = inspections?.find((inspection) => inspection.id === inspectionId);

  const [hasReadInspectionPermission, hasCreateInspectionPermission] = usePermissions({
    permissionKeys: ['readInspection', 'createInspection'],
    scopes: {
      readInspection: {
        inspectionType: currentInspection?.inspectionType,
        // Gated by inspection type, not participation
        participation: 'SKIP',
      },
      createInspection: {inspectionType: currentInspection?.inspectionType},
    },
  });

  useHandleForeignInspection(id, currentInspection);

  const navigationTabs = buildArray<NavigationTab>()
    .when(isSaleVehicle || isServiceVehicle, {
      id: TAB_IDS.VEHICLE_CONDITION,
      label: i18n.t('entity.vehicle.labels.vehicleCondition'),
      route: vehiclesRoutes.conditionDetail,
    })
    .when(hasReadInspectionPermission, {
      id: TAB_IDS.INSPECTIONS,
      label: i18n.t('entity.vehicle.labels.inspections'),
      route: vehiclesRoutes.conditionsInspections,
    });

  if (inspectionId && id) {
    return (
      <Audit
        data-testid="inspection"
        notFoundText={i18n.t('entity.condition.notifications.inspectionNotFound')}
        vehicleId={id}
        header={
          <InspectionHeader
            data-testid="inspection-header"
            inspectionId={inspectionId}
            vehicleId={id}
          />
        }
      />
    );
  }

  const activeTabId =
    navigationTabs.find((navigationTab) => navigationTab.route === router.route)?.id ??
    navigationTabs[0]?.id;

  const handleChangeTab = (tabId: string) => {
    const nextTabRoute = navigationTabs.find((item) => item.id === tabId)?.route as `${string}/:id`;

    if (nextTabRoute) {
      router.push(composePath(nextTabRoute, {params: {id}}));
    }
  };

  const tabs = buildArray<TabProps>()
    .when(isSaleVehicle || isServiceVehicle, {
      id: TAB_IDS.VEHICLE_CONDITION,
      title: i18n.t('entity.vehicle.labels.vehicleTechnicalCondition'),
      'data-testid': testIds.vehicles.conditions('condition'),
      content: (
        <Audit
          data-testid={testIds.vehicles.conditions('auditTab')}
          notFoundText={i18n.t('entity.condition.notifications.vehicleConditionNotFound')}
          vehicleId={id}
          isVehicleCondition
          header={
            <ConditionHeaderPortal
              portalTarget={portalTarget}
              vehicleId={id}
              inspectionId={activeInspectionId}
              data-testid={testIds.vehicles.conditions('auditTab')}
              shouldUseAuditIdForUpdateTemplate={props.shouldUseAuditIdForUpdateTemplate}
            />
          }
        />
      ),
    })
    .when(hasReadInspectionPermission, {
      id: TAB_IDS.INSPECTIONS,
      title: i18n.t('entity.vehicle.labels.inspections'),
      'data-testid': testIds.vehicles.conditions('inspections'),
      content: (
        <Inspections
          data-testid={testIds.vehicles.conditions('inspections-section')}
          getRedirectRoute={(_id, _inspectionId) =>
            composePath(vehiclesRoutes.inspectionDetail, {
              params: {
                id: _id,
                inspectionId: _inspectionId,
              },
            })
          }
        />
      ),
    });

  const tabContent = (tabs.find((item) => item.id === activeTabId) ?? tabs[0])?.content;

  return (
    <>
      <HStack justify="space-between" width="100%" spacing={4} wrap>
        <TabsHeader
          variant="condensed"
          onChange={handleChangeTab}
          activeTabId={activeTabId}
          tabs={tabs}
          data-testid={testIds.vehicles.conditionDetail()}
        />
        <HStack spacing={2} justify="flex-end" grow={1} wrap>
          <Show when={activeTabId === TAB_IDS.VEHICLE_CONDITION}>
            <div ref={setPortalTarget} style={{display: 'contents'}} />
          </Show>
          <Show when={activeTabId === TAB_IDS.INSPECTIONS && hasCreateInspectionPermission}>
            <NewInspectionButton />
          </Show>
        </HStack>
      </HStack>

      <Space vertical={4} />

      {tabContent}
    </>
  );
}
